import { useState, useEffect } from 'react';

const HeroSection = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  
  const slides = [
    {
      title: "WORKPLACE INJURIES COST COMPANIES $170 BILLION ANNUALLY",
      subtitle: "Protect Your Investment with Professional Safety Solutions",
      highlight: "Safety Staffing & Industrial Hygiene Excellence"
    },
    {
      title: "STAY SAFE®",
      subtitle: "Your Partner in OSHA Compliance Excellence",
      highlight: "Specialized Safety Staffing Solutions"
    },
    {
      title: "NEW EMPLOYEES FACE 3X HIGHER INJURY RISK IN FIRST 30 DAYS",
      subtitle: "Comprehensive Training Programs for Every Industry",
      highlight: "Industrial Hygiene Monitoring & Assessment"
    },
    {
      title: "STAY TRAINED®",
      subtitle: "Expert Safety Training for Heavy Civil & Industrial Projects",
      highlight: "On-Site Safety Professionals Available"
    },
    {
      title: "EFFECTIVE SAFETY PROGRAMS REDUCE INCIDENTS BY 40%",
      subtitle: "Proven Strategies for Construction & Manufacturing",
      highlight: "Environmental Health & Safety Services"
    },
    {
      title: "BRIDGE & STRUCTURAL STEEL SAFETY EXPERTISE",
      subtitle: "Specialized Solutions for Large-Scale Transportation Projects",
      highlight: "Safety Staffing for Heavy Civil Projects"
    }
  ];

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 7000);
    return () => clearInterval(timer);
  }, [slides.length]);

  const goToSlide = (index: number) => {
    setCurrentSlide(index);
  };

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % slides.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
  };

  return (
    <>
      {/* Welcome Section */}
      <section className="bg-gradient-to-br from-orange-50 to-yellow-50 py-16">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold text-gray-800 mb-4">Welcome to HP Inc Consulting Services</h2>
          <h4 className="text-xl text-orange-600 font-semibold mb-6">A Premier Safety, Environmental & Coating Inspection Consulting Company</h4>
          <div className="max-w-4xl mx-auto">
            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              HP Inc Consulting Services is a premier safety and coating inspection consulting firm delivering comprehensive OSHA compliance services and professional coating inspection solutions to heavy civil construction, industrial projects, and marine applications. Our expertise spans bridges, structural steel, major transportation infrastructure, and industrial coating systems. As a certified MBE, DBE, SBE C Corporation, we are uniquely positioned to assist with all safety consulting and coating inspection requirements while ensuring the highest standards of worker protection, coating quality control, and regulatory compliance across diverse industries.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-8">
              <div className="bg-white p-6 rounded-lg shadow-lg border-l-4 border-orange-500">
                <div className="flex items-center mb-4">
                  <i className="ri-team-fill text-2xl text-orange-500 mr-3"></i>
                  <h3 className="text-lg font-bold text-gray-800">OSHA Training</h3>
                </div>
                <p className="text-gray-600 text-sm">
                  Comprehensive safety training programs designed to meet OSHA standards and protect your workforce from workplace hazards.
                </p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-lg border-l-4 border-orange-500">
                <div className="flex items-center mb-4">
                  <i className="ri-shield-check-fill text-2xl text-orange-500 mr-3"></i>
                  <h3 className="text-lg font-bold text-gray-800">OSHA Consulting</h3>
                </div>
                <p className="text-gray-600 text-sm">
                  Expert guidance on OSHA compliance, safety program development, and regulatory requirements to keep your operations safe and compliant.
                </p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-lg border-l-4 border-orange-500">
                <div className="flex items-center mb-4">
                  <i className="ri-paint-brush-fill text-2xl text-orange-500 mr-3"></i>
                  <h3 className="text-lg font-bold text-gray-800">Coating Inspection</h3>
                </div>
                <p className="text-gray-600 text-sm">
                  Professional coating inspection services including surface preparation assessment, application monitoring, and quality control for industrial and marine coating systems.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Hero Slider */}
      <section className="relative h-96 bg-gradient-to-r from-orange-900 to-blue-900 overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: `url('https://public.readdy.ai/ai/img_res/f7d950b96ac8df075bf8df60b448a9cd.jpg')`
          }}
        >
          <div className="absolute inset-0 safety-hero-overlay"></div>
        </div>
        
        <div className="relative h-full flex items-center justify-center">
          <div className="container mx-auto px-4 text-center">
            <div className="max-w-4xl mx-auto">
              <div className="mb-4">
                <span className="inline-block bg-yellow-400 text-gray-900 px-4 py-2 rounded-full text-sm font-semibold">
                  {slides[currentSlide].highlight}
                </span>
              </div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4 leading-tight">
                {slides[currentSlide].title}
              </h1>
              <p className="text-xl md:text-2xl text-orange-200 mb-8">
                {slides[currentSlide].subtitle}
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <button className="safety-button">
                  Get Safety Staffing Quote
                </button>
                <button className="safety-button-secondary">
                  Industrial Hygiene Services
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Navigation Arrows */}
        <button 
          onClick={prevSlide}
          className="absolute left-4 top-1/2 transform -translate-y-1/2 w-12 h-12 bg-white/20 hover:bg-white/30 rounded-full flex items-center justify-center text-white transition-all duration-200"
        >
          <i className="ri-arrow-left-line text-xl"></i>
        </button>
        <button 
          onClick={nextSlide}
          className="absolute right-4 top-1/2 transform -translate-y-1/2 w-12 h-12 bg-white/20 hover:bg-white/30 rounded-full flex items-center justify-center text-white transition-all duration-200"
        >
          <i className="ri-arrow-right-line text-xl"></i>
        </button>

        {/* Slide Indicators */}
        <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex space-x-2">
          {slides.map((_, index) => (
            <button
              key={index}
              onClick={() => goToSlide(index)}
              className={`w-3 h-3 rounded-full transition-all duration-200 ${
                index === currentSlide ? 'bg-yellow-400' : 'bg-white/50 hover:bg-white/70'
              }`}
            />
          ))}
        </div>
      </section>
    </>
  );
};

export default HeroSection;
